import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { Capabilities } from "@/components/calv/Capabilities";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/social-media-advertising")({
  head: () => {
    const h = pageHead("pmSocialMediaWerbung", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Social Media Advertising — Paid Social Marketing",
          description: "Paid Social Marketing for Meta, LinkedIn, TikTok from Karlstadt am Main, Germany. Performance discipline, no content playground, platform-critical and economically measurable.",
          url: "/en/services/performance-marketing/social-media-advertising",
          serviceType: "Paid Social Advertising",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "Social Media Advertising", path: "/en/services/performance-marketing/social-media-advertising" },
        ])),
      ],
    };
  },
  component: SocialMediaAdvertisingEN,
});

const platforms = [
  { tag: "Meta Ads", title: "Facebook & Instagram", body: "The economically broadest advertising infrastructure in DACH — about 92% of 16–64-year-olds are active. Strong for e-commerce scaling, B2C lead gen, employee recruitment, broad brand building. Weak for highly specialized B2B profiles." },
  { tag: "LinkedIn Ads", title: "B2B decision-makers", body: "The only platform with substantial B2B targeting by position, industry, company size, and function. Strong for B2B lead gen, account-based marketing, consulting services. Weak for B2C mass markets (CPCs too high)." },
  { tag: "TikTok Ads", title: "Younger audiences", body: "Growth-strongest platform for 16–35-year-olds in DACH. Strong for B2C consumer goods with visual conveyability, trend-driven markets, recruiting for career entrants. Weak for B2B and high-priced consulting." },
  { tag: "Methodological bracket", title: "Across all platforms", body: "Smart Bidding, server-side conversion tracking (CAPI / Conversions API / Events API), conversion feedback from the CRM, and continuous creative maintenance — standard, not premium." },
];

const capabilities = [
  { name: "Meta Ads (Facebook · Instagram)", detail: "E-commerce · B2C · recruiting" },
  { name: "LinkedIn Ads", detail: "B2B lead gen · ABM" },
  { name: "TikTok Ads", detail: "Young B2C · trend markets" },
  { name: "Server-side conversion tracking", detail: "CAPI · Conversions API · Events API" },
  { name: "CRM → ad platform feedback", detail: "Qualified conversion signals" },
  { name: "Smart Bidding management", detail: "Target CPA · Target ROAS · Max Conv" },
  { name: "Creative refresh pipeline", detail: "Monthly cadence, picazzoo-supported" },
  { name: "Platform combination logic", detail: "Platform choice as economic question" },
];

const fitWhen = [
  { tag: "Fits", title: "B2C e-commerce with visual products", body: "Fashion, furniture, consumer goods, food specialties. Target ROAS of 300–800% achievable in clean setups — depending on margins and competition." },
  { tag: "Fits", title: "B2C lead gen with short sales cycles", body: "Insurance, financial services, contracts with switching logic, booking platforms. Paid Social often beats Google Ads here because passive switch-willing users can be addressed." },
  { tag: "Fits", title: "Employee recruitment in mid-market industries", body: "Care, trades, logistics, manufacturing, hospitality, education roles. Cost-per-application 60–90% below classical job portals." },
  { tag: "Fits", title: "B2B lead gen with defined decision-maker audience", body: "Mid-market B2B SaaS, IT service providers, consultants. LinkedIn as the methodologically clean platform — CPL higher (€80–300), lead quality correspondingly better." },
];

const fitWhenNot = [
  { tag: "Doesn't fit", title: "High-priced consulting with long sales cycles", body: "Strategy and M&A advisory in six-figure fee ranges. Decision paths too complex for performance algorithms — classical active sourcing and referral marketing more rational." },
  { tag: "Doesn't fit", title: "Highly regulated advertising areas", body: "Areas in finance, pharma, gambling, insurance with narrow advertising restrictions. Platform bans limit economic controllability." },
  { tag: "Doesn't fit", title: "Very narrow niche markets", body: "When the German-speaking audience is < 5,000–10,000 people, conversion volume is insufficient for algorithm learning cycles. SEO authority and direct acquisition cleaner." },
  { tag: "Doesn't fit", title: "Highly specialized technical B2B profiles", body: "IT architects, specialist engineers. Even LinkedIn rarely reaches these profiles reliably — active sourcing via XING, GitHub, Stack Overflow is superior." },
];

const entryPaths = [
  ["Single campaign for a concrete objective.", "Recruiting for 2–3 positions, seasonal e-commerce scaling, lead gen for one concrete product. 7–14 days from briefing to live, from €1,500 media spend plus setup."],
  ["Ongoing Paid Social mandate.", "Continuous accompaniment with media spend €2,000–€25,000/month. Creative maintenance, funnel optimization, tracking maintenance, monthly reporting. Methodical work across all active platforms."],
  ["Audit of an existing setup.", "You already run Paid Social — yourself or via another agency. 6–12 hours audit effort, output: concrete findings report with optimization levers and effort estimates."],
];

const subDisciplines = [
  { to: "/en/services/performance-marketing/meta-ads", name: "Meta Ads", sub: "Facebook & Instagram for e-commerce, B2C SaaS, and visually communicable B2B" },
  { to: "/en/services/performance-marketing/linkedin-ads", name: "LinkedIn Ads", sub: "B2B decision-maker targeting for lead gen, ABM, and enterprise SaaS" },
  { to: "/en/services/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Visual performance for younger B2C audiences and trend-driven markets" },
];

function SocialMediaAdvertisingEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Paid Social"
          headline={<>Social Media Advertising — <em>not a content playground,</em> but a performance channel.</>}
          lead="Calvarius has run Paid Social Advertising on Meta, LinkedIn, and TikTok since 2013 — for mid-market mandates from Main-Spessart, Würzburg, Aschaffenburg, Frankfurt, and nationwide. With measurable economic efficiency, clean server-side conversion tracking, and the same platform-critical discipline that characterizes our Google Ads and SEO work. Those looking for a classical social media agency are in the wrong place. Those who want Paid Social as an economically rational advertising channel are right here."
          metaStrip={[
            "In the Paid Social channel since 2013",
            "Meta · LinkedIn · TikTok",
            "Karlstadt am Main · nationwide",
          ]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "Social Media Advertising" },
          ]}
        />

        <Section number="02" kicker="Standpoint" headline={<>What Paid Social at Calvarius is — <em>and what it is not.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">The term "social media agency" is imprecise in DACH — it can simultaneously designate three very different service packages that are economically barely comparable. Clear demarcation is the most important differentiating act here.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Variant A — classical social media agency.</strong> Content maintenance on client profiles: posting plans, content production, community responses, organic reach work. Economic value predominantly brand building — short-term conversion impact low to none.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Variant B — influencer & creator agency.</strong> Mediation between brands and influencers, contract design, content briefing. Methodologically a different discipline than performance advertising.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Variant C — Paid Social agency.</strong> Exclusively ad placement via the ad managers of Meta, LinkedIn, TikTok. Targeting, creative maintenance, conversion tracking, budget steering, algorithm care. Economically measurable via CPA and ROAS.</p>
              <p>Calvarius operates exclusively Variant C. Those seeking organic posting or influencer mediation are better served at classical agencies — we are happy to recommend specialists when needed.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Platform logic" headline={<>Three platforms, <em>three economic applications.</em></>}
          caption="Platform choice per mandate is an economic question — not a faith question.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {platforms.map((p) => (
                <div key={p.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{p.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{p.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Why no content" headline={<>Why we <em>don't do content</em> — and what that means economically.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5"><strong className="font-semibold text-ink">First: organic reach has been structurally declining for years.</strong> On Facebook, company posts now reach only 1–3% of own followers, on Instagram 5–9%, on LinkedIn 3–7%. Platforms deliberately suppress organic reach in favor of paid — those investing in content maintenance in 2026 invest in a shrinking channel.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Second: content maintenance is methodologically a different discipline.</strong> A good content strategist isn't automatically a good performance marketer. Cross-subsidization, where performance agencies do content on the side, hurts both disciplines.</p>
              <p>
                <strong className="font-semibold text-ink">Third: the economic lever almost always lies in ad placement, not in posting.</strong> With a mandate at €5,000 marketing budget, €4,000 Paid Social spend plus €1,000 consulting typically delivers 10–30× more conversions than €5,000 for content without ad placement. Not prejudice — twelve years of mandate observation.
              </p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="When it fits" headline={<>When Paid Social is <em>the right channel.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {fitWhen.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="When it doesn't" headline={<>When Paid Social is <em>not the right channel.</em></>}
          caption="Honest limitation statements belong to the Calvarius initial consultation.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {fitWhenNot.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-2">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Methodological bracket" headline={<>Four disciplines that apply <em>across all platforms.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5"><strong className="font-semibold text-ink">Smart Bidding instead of manual control.</strong> Manual bid management is no longer competitive on any of the three platforms. We work with <Link to="/en/glossary/smart-bidding" className="underline">Smart Bidding</Link> strategies — Target CPA, Target ROAS, Maximize Conversions — provided algorithms are fed with the right conversion signals.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Server-side conversion tracking as standard.</strong> Classical pixel tracking today sees only 40–70% of actual conversions. We implement CAPI at Meta, Conversions API at LinkedIn, and Events API at TikTok routinely — not as a premium feature.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Conversion feedback from the CRM.</strong> With B2B lead gen and e-commerce with return rates, the feedback of qualified conversions to ad platforms is the decisive performance lever. <Link to="/en/blog/crm-ad-platform-integration" className="underline">More on CRM-to-ad-platform integration</Link>.</p>
              <p><strong className="font-semibold text-ink">Creative discipline as ongoing maintenance.</strong> The best ad performances exhaust themselves after 4–8 weeks. Without continuous refresh pipeline, the best setup becomes inefficient after 2–3 months. We work in monthly creative refresh cadence — picazzoo-supported.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What we deliver" headline={<>What our <em>Paid Social management</em> concretely contains.</>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section variant="tinted" number="09" kicker="Local anchoring" headline={<>From Karlstadt am Main — <em>on-site and nationwide.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Calvarius works from Karlstadt am Main — geographically in the heart of Main-Spessart, with short routes to <Link to="/en/regions/wuerzburg" className="underline">Würzburg</Link> (25 km), <Link to="/en/regions/schweinfurt" className="underline">Schweinfurt</Link> (40 km), <Link to="/en/regions/aschaffenburg" className="underline">Aschaffenburg</Link> (50 km), and <Link to="/en/regions/frankfurt" className="underline">Frankfurt</Link> (100 km). Regional anchoring is not only location information but service element — on-site appointments for initial briefings, strategy sessions, or larger reportings are natural in the region.</p>
              <p>While the on-site component is locally anchored, the digital advertising work is nationwide and DACH-wide available. Mandates from Berlin, Hamburg, Munich, Vienna, or Zurich are methodologically maintained identically.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Entry paths" headline={<>Three typical <em>entry paths into mandates.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {entryPaths.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="11" kicker="Sub-disciplines" headline={<>Platform detail pages.</>}
          caption="Each platform has its own mechanics — described on the detail pages.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {subDisciplines.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-6">
              <Link to="/en/services/performance-marketing/meta-ads/recruiting" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                Meta Ads for employee recruitment →
              </Link>
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="12" kicker="Get concrete">
          <CTABlock
            headline={<>Paid Social audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation we clarify whether Paid Social is the right channel for your economic constellation — and if not, we say so honestly and recommend alternatives."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
